import React, { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import WaterDropIcon from '@mui/icons-material/WaterDrop';

/* Contenedor centrado y responsive (igual que en los otros) */
const containerSx = (hideBelowWidth = 0) => ({
  display: "flex",
  justifyContent: "center",
  pointerEvents: "none",
  mt: -1,
  opacity: 1,
  transition: "opacity .25s ease",
  [`@media (max-width:${hideBelowWidth}px)`]: { opacity: 0 },
});

// Estiliza sólo los números dentro del texto (p. ej. "0.5 – 1")
function renderLabelWithThinNumbers(label) {
  const parts = String(label).split(/(\d+(?:[.,]\d+)?)/g); // captura 0, 0.5, 32, etc.
  return parts.map((part, i) => {
    const isNumber = /\d/.test(part);
    return (
      <Box
        key={i}
        component="span"
        sx={
          isNumber
            ? {
                fontWeight: 450,           // un poco más visible
                fontSize: '0.92em',        
                letterSpacing: '.005em',
                fontVariantNumeric: 'tabular-nums', 
              }
            : undefined
        }
      >
        {part}
      </Box>
    );
  });
}

/* Bins (mm/h) con colores sólidos */
const LEVELS = [
  { label: "0.08 – 0.5", range: [0.08, 0.5],  color: "#3e3efc" },
  { label: "0.5 – 1",    range: [0.5, 1],     color: "#5a82f5" },
  { label: "1 – 2",      range: [1, 2],       color: "#46cafb" },
  { label: "2 – 4",      range: [2, 4],       color: "#35b037" },
  { label: "4 – 8",      range: [4, 8],       color: "#f3ce37" },
  { label: "8 – 16",     range: [8, 16],      color: "#fdb03e" },
  { label: "16 – 32",    range: [16, 32],     color: "#f33637" },
  { label: "32 – 160",   range: [32, 160],    color: "#B30000" },
];

/* Texto blanco/negro automático por contraste */
function textColorFor(hex) {
  const c = hex.replace("#", "");
  const r = parseInt(c.substring(0, 2), 16);
  const g = parseInt(c.substring(2, 4), 16);
  const b = parseInt(c.substring(4, 6), 16);
  // luminancia YIQ rápida
  const yiq = (r * 299 + g * 587 + b * 114) / 1000;
  return yiq >= 150 ? "#0B1220" : "#FFFFFF";
}

export default function RiskSelectorNowcasting({
  onSelectRiskLevel,
  hideBelowWidth = 0,
  interactive = false,
  size = "mini",       // "mini" | "md"
  equalWidth = false,  // con 8 bins suele quedar mejor sin forzar ancho
  showTitle = true,
  titleText = "Nowcasting (mm/h)",
}) {
  const [selectedRisk, setSelectedRisk] = useState("");
  useEffect(() => {
    // puedes mostrar un tooltip si quisieras; lo omitimos para mantener limpio
  }, []);

  const isMini = size === "mini";
  const pillMin = equalWidth ? (isMini ? 92 : 108) : 0;

  return (
    <Box>
      {/* Título sutil centrado con icono (mismo estilo tipográfico) */}
      {showTitle && (
        <Box sx={{ display: "grid", justifyItems: "center", pointerEvents: "none", mb: 0.95 }}>
          <Box
            component="span"
            sx={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 0.5,
              typography: "caption",
              fontWeight: 750,
              letterSpacing: ".025em",
              textTransform: "uppercase",
              color: "#23339b",
              lineHeight: 1.05,
            }}
          >
            <WaterDropIcon sx={{ fontSize: { xs: 12, sm: 12, md: 13 }, color: "inherit" }} aria-hidden />
            {titleText}
          </Box>
        </Box>
      )}

      {/* Píldoras (8 bins) con estilo unificado */}
      <Box sx={containerSx(hideBelowWidth)}>
        <Box
          sx={{
            display: "inline-flex",
            borderRadius: 999,
            overflow: "hidden",
            pointerEvents: interactive ? "auto" : "none",
          }}
        >
          {LEVELS.map((lvl, i) => {
            const key = `${lvl.range[0]}-${lvl.range[1]}`;
            const isSelected = selectedRisk === key;
            const fg = textColorFor(lvl.color);

            return (
              <Button
                key={key}
                disableElevation
                disableRipple
                aria-label={`${lvl.label} mm/h`}
                onClick={
                  interactive
                    ? () => {
                        const next = isSelected ? "" : key;
                        setSelectedRisk(next);
                        onSelectRiskLevel?.(next ? lvl.range : "");
                      }
                    : undefined
                }
                sx={{
                  position: "relative",
                  minWidth: equalWidth ? (isMini ? 42 : 54) : 0,
                  px: isMini ? 0.8 : 1.0,
                  py: isMini ? 0.4 : 0.5,
                  fontSize: isMini ? ".62rem" : ".7rem",
                  fontWeight: 600,          // texto más visible
                  letterSpacing: ".02em",
                  lineHeight: 1,
                  textTransform: "none",
                  color: textColorFor(lvl.color),
                  backgroundColor: `rgba(${parseInt(lvl.color.slice(1, 3), 16)}, ${parseInt(lvl.color.slice(3, 5), 16)}, ${parseInt(lvl.color.slice(5, 7), 16)}, 0.8)`,
                  backdropFilter: "blur(20px) saturate(190%) contrast(1.1)",
                  borderRadius: 0,
                  border: "1px solid rgba(255, 255, 255, 0.25)",
                  borderTop: "2.5px solid rgba(255, 255, 255, 0.5)",
                  borderRight: i < LEVELS.length - 1 ? 0 : "1px solid rgba(255, 255, 255, 0.25)",
                  boxShadow: `
                    0 12px 40px 0 rgba(0, 0, 0, 0.25),
                    inset 0 1px 1px 0 rgba(255, 255, 255, 0.3),
                    inset 0 -1px 0px 0 rgba(0, 0, 0, 0.1)
                  `,
                  transition: "transform .2s cubic-bezier(0.4, 0, 0.2, 1), background-color .25s ease, box-shadow .2s ease",
                  cursor: interactive ? "pointer" : "default",
                  "&:hover": {
                    transform: interactive ? "scale(1.02) translateY(-2px)" : "none",
                    backgroundColor: `rgba(${parseInt(lvl.color.slice(1, 3), 16)}, ${parseInt(lvl.color.slice(3, 5), 16)}, ${parseInt(lvl.color.slice(5, 7), 16)}, 0.9)`,
                    boxShadow: "0 15px 45px 0 rgba(0, 0, 0, 0.3)",
                    zIndex: 2,
                  },
                }}
              >
                <Box component="span">{renderLabelWithThinNumbers(lvl.label)}</Box>

              </Button>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
}
